import Vue from "vue";

export default (_, inject) => {
  const releaseStages = "production,staging,development"
    .split(",")
    .map(releaseStage => releaseStage.trim());

  const releaseStage = "production";
  const isEnabled = releaseStages.includes(releaseStage);

  // If disabled (e.g., local development), register a lightweight dummy mock
  if (!isEnabled) {
    const mockBugsnag = {
      setContext() {},
      addMetadata() {},
      clearMetadata() {},
      notify(err) {
        console.error("[Bugsnag Mock] Error reported:", err);
      },
      leaveBreadcrumb() {},
      notifyApiError({ err, url } = {}) {
        console.error(
          "[Bugsnag Mock] API Error reported:",
          err,
          "at URL:",
          url
        );
      },
    };

    const proxyMock = new Proxy(mockBugsnag, {
      get(target, prop) {
        if (prop in target) {
          return target[prop];
        }
        return () => {};
      },
    });

    inject("bugsnag", proxyMock);
    return;
  }

  // Server-side (SSR) execution: Initialize server-compatible Bugsnag client synchronously to report SSR errors
  if (process.server) {
    const req = typeof require !== "undefined" ? require : null;
    if (req) {
      try {
        const BugsnagModule = req("@bugsnag/js");
        const Bugsnag = BugsnagModule.default || BugsnagModule;
        const BugsnagPluginVueModule = req("@bugsnag/plugin-vue");
        const BugsnagPluginVue =
          BugsnagPluginVueModule.default || BugsnagPluginVueModule;

        let bugsnagClient = Bugsnag;
        if (!Bugsnag.isStarted()) {
          const serverOptions = {
            plugins: [new BugsnagPluginVue()],
            appType: "storefront-app",
          };
          bugsnagClient = Bugsnag.start({
            apiKey: "4f2096c1cecbd0e8c29361763207fe3b",
            appVersion: "1.64",
            releaseStage: "production",

            onError: function (event) {
              if (!releaseStages.includes(event.app.releaseStage)) {
                console.error(event.errors);
                return false;
              }
              return true;
            },
            ...serverOptions,
          });
          Bugsnag.getPlugin("vue")?.installVueErrorHandler?.(Vue);
        }

        const myBugSnagClient = {
          ...bugsnagClient,
          setContext: bugsnagClient.setContext,
          addMetadata: bugsnagClient.addMetadata,
          clearMetadata: bugsnagClient.clearMetadata,
          notifyApiError({ err, url, body, header } = {}) {
            bugsnagClient.notify(err, e =>
              e.addMetadata("my request", { url, body, header })
            );
          },
        };

        const proxyServer = new Proxy(myBugSnagClient, {
          get(target, prop) {
            if (prop in target) {
              return target[prop];
            }
            const val = bugsnagClient[prop];
            return typeof val === "function" ? val.bind(bugsnagClient) : val;
          },
        });

        inject("bugsnag", proxyServer);
        return;
      } catch (e) {
        console.error("[Bugsnag Server] Failed to initialize:", e);
      }
    }

    const fallbackServerBugsnag = {
      setContext() {},
      addMetadata() {},
      clearMetadata() {},
      notify() {},
      leaveBreadcrumb() {},
      notifyApiError() {},
    };

    const proxyFallbackServer = new Proxy(fallbackServerBugsnag, {
      get(target, prop) {
        if (prop in target) {
          return target[prop];
        }
        return () => {};
      },
    });

    inject("bugsnag", proxyFallbackServer);
    return;
  }

  // --- CLIENT SIDE (ENABLED) ---
  const earlyErrors = [];
  const handleEarlyError = event => {
    earlyErrors.push({
      type: "error",
      args: [event.error || new Error(event.message || "Unknown error")],
    });
  };
  const handleEarlyRejection = event => {
    earlyErrors.push({ type: "rejection", args: [event.reason] });
  };

  // Register immediate global error listeners to catch early boot/hydration issues
  window.addEventListener("error", handleEarlyError);
  window.addEventListener("unhandledrejection", handleEarlyRejection);

  // Intercept Vue configuration errors
  const originalErrorHandler = Vue.config.errorHandler;
  Vue.config.errorHandler = (err, vm, info) => {
    earlyErrors.push({ type: "vue", args: [err, vm, info] });
    if (originalErrorHandler) {
      originalErrorHandler(err, vm, info);
    } else {
      console.error(err);
    }
  };

  let bugsnagClient = null;
  const queue = [];

  const myBugSnagClient = {
    setContext(ctx) {
      if (bugsnagClient) bugsnagClient.setContext(ctx);
      else queue.push({ method: "setContext", args: [ctx] });
    },
    addMetadata(section, data) {
      if (bugsnagClient) bugsnagClient.addMetadata(section, data);
      else queue.push({ method: "addMetadata", args: [section, data] });
    },
    clearMetadata(section, key) {
      if (bugsnagClient) bugsnagClient.clearMetadata(section, key);
      else queue.push({ method: "clearMetadata", args: [section, key] });
    },
    notify(err, cb) {
      if (bugsnagClient) bugsnagClient.notify(err, cb);
      else queue.push({ method: "notify", args: [err, cb] });
    },
    leaveBreadcrumb(name, metadata, type) {
      if (bugsnagClient) bugsnagClient.leaveBreadcrumb(name, metadata, type);
      else
        queue.push({ method: "leaveBreadcrumb", args: [name, metadata, type] });
    },
    notifyApiError({ err, url, body, header } = {}) {
      if (bugsnagClient) {
        bugsnagClient.notify(err, e =>
          e.addMetadata("my request", { url, body, header })
        );
      } else {
        queue.push({
          method: "notifyApiError",
          args: [{ err, url, body, header }],
        });
      }
    },
  };

  const proxyBugsnag = new Proxy(myBugSnagClient, {
    get(target, prop) {
      if (prop === "then") return undefined;
      if (typeof prop === "symbol") return target[prop];
      if (prop in target) {
        return target[prop];
      }
      if (bugsnagClient) {
        const val = bugsnagClient[prop];
        return typeof val === "function" ? val.bind(bugsnagClient) : val;
      }
      return (...args) => {
        queue.push({ method: prop, args });
      };
    },
  });

  inject("bugsnag", proxyBugsnag);

  // Lazy-load Bugsnag asynchronously when main thread is idle
  const initBugsnag = async () => {
    try {
      console.log("[Bugsnag] Loading SDK in background...");
      const [BugsnagModule, BugsnagVueModule] = await Promise.all([
        import("@bugsnag/js"),
        import("@bugsnag/plugin-vue"),
      ]);

      const Bugsnag = BugsnagModule.default;
      const BugsnagPluginVue = BugsnagVueModule.default;

      const options = {
        plugins: [new BugsnagPluginVue()],
        appType: "storefront-app",
      };

      if (!Bugsnag.isStarted()) {
        bugsnagClient = Bugsnag.start({
          apiKey: "4f2096c1cecbd0e8c29361763207fe3b",
          appVersion: "1.64",
          releaseStage: "production",

          onError: function (event) {
            const msg = event.errors?.[0]?.errorMessage || "";

            // Ignore iOS WebKit bridge noise
            if (
              msg.includes("window.webkit.messageHandlers") ||
              msg.includes("_AutofillCallbackHandler")
            ) {
              return false;
            }

            if (!releaseStages.includes(event.app.releaseStage)) {
              console.error(event.errors);
              return false;
            }

            return true;
          },
          ...options,
        });
        Bugsnag.getPlugin("vue").installVueErrorHandler(Vue);
      } else {
        bugsnagClient = Bugsnag;
      }

      console.log("[Bugsnag] SDK initialized successfully.");

      // Clean up temporary listeners
      window.removeEventListener("error", handleEarlyError);
      window.removeEventListener("unhandledrejection", handleEarlyRejection);

      // Replay early captured errors
      if (earlyErrors.length > 0) {
        console.log(
          `[Bugsnag] Replaying ${earlyErrors.length} early errors...`
        );
        earlyErrors.forEach(errEvent => {
          if (errEvent.type === "vue") {
            const [err, , info] = errEvent.args;
            bugsnagClient.notify(err, event => {
              event.addMetadata("vue", { info });
            });
          } else {
            const [err] = errEvent.args;
            bugsnagClient.notify(err);
          }
        });
      }

      // Replay queued calls
      if (queue.length > 0) {
        console.log(
          `[Bugsnag] Replaying ${queue.length} queued method calls...`
        );
        queue.forEach(item => {
          if (item.method === "notifyApiError") {
            const { err, url, body, header } = item.args[0];
            bugsnagClient.notify(err, e =>
              e.addMetadata("my request", { url, body, header })
            );
          } else {
            bugsnagClient[item.method](...item.args);
          }
        });
      }
    } catch (err) {
      window.removeEventListener("error", handleEarlyError);
      window.removeEventListener("unhandledrejection", handleEarlyRejection);
      Vue.config.errorHandler = originalErrorHandler;
      console.error("[Bugsnag] Failed to load or initialize dynamically:", err);
    }
  };

  // Run on idle or fallback to 2 seconds
  if (window.requestIdleCallback) {
    window.requestIdleCallback(() => initBugsnag());
  } else {
    setTimeout(initBugsnag, 2000);
  }
};
